import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Map } from "immutable";
import { Button, Card, Divider, Form, message, Spin, Alert } from "antd";
import cx from "classnames";
import {
    fieldGenerator,
    getErrorOnField
} from "utilities/form";
import {
    INPUT_TYPES,
    CEP_CREDIT_APPRAISAL_FORM_FIELDS,
    LAP_CREDIT_APPRAISAL_FORM_FIELDS,
    PERSONAL_LOAN_CREDIT_APPRAISAL_FORM_FIELDS
} from "constants";
import { CaseService } from "../../services";

const FormItem = Form.Item;
const DATE_FORMAT = "YYYY-MM-DD";

const SCREEN_FIELD_MAP = {
    CEP: CEP_CREDIT_APPRAISAL_FORM_FIELDS,
    LAP: LAP_CREDIT_APPRAISAL_FORM_FIELDS,
    PERSONAL_LOAN: PERSONAL_LOAN_CREDIT_APPRAISAL_FORM_FIELDS
};

class ProductDetailsForm extends React.Component {
    fieldGenerator = fieldGenerator.bind(this.props.form);
    getErrorOnField = getErrorOnField.bind(this.props.form);

    handleSubmit = (e) => {
        e.preventDefault();
        this.props.form.validateFields((err, values) => {
            if (err) {
                return;
            }
            const payload = {};
            Object.keys(values).forEach((key) => {
                const value = values[key];
                payload[key] =
                    value && typeof value.format === "function"
                        ? value.format(DATE_FORMAT)
                        : value;
            });
            CaseService.updateCreditAppraisalDetails(this.props.caseId, payload)
                .then(() => {
                    message.success("Credit appraisal details saved.");
                })
                .catch((error) => {
                    const errors =
                        error &&
                        error.response &&
                        error.response.data &&
                        error.response.data.non_field_errors;
                    message.error(
                        (errors && errors[0]) || "Failed to save credit appraisal details."
                    );
                });
        });
    };

    renderSections = () => {
        const sections = this.props.fields
            .groupBy((field) => field.section || "")
            .map((sectionFields, sectionName) => (
                <div key={sectionName || "default"} className={"w-100"}>
                    {sectionName && (
                        <Divider orientation={"left"} className={"mb-5"}>
                            <span className={"fw-600"}>{sectionName}</span>
                        </Divider>
                    )}
                    <div className={"d-flex flex-row align-items-end flex-wrap w-100"}>
                        {sectionFields
                            .map((field, fieldName) =>
                                this.fieldGenerator(
                                    fieldName,
                                    field,
                                    this.getErrorOnField(fieldName)
                                )()
                            )
                            .toList()
                            .toJS()}
                    </div>
                </div>
            ));
        return sections.toList().toJS();
    };

    render() {
        return (
            <Form onSubmit={this.handleSubmit} className={cx("form", "br-3")}>
                {this.renderSections()}
                {!this.props.viewOnly && (
                    <FormItem className={"mt-10"}>
                        <Button type="primary" htmlType="submit">
                            Save
                        </Button>
                    </FormItem>
                )}
            </Form>
        );
    }
}

const WrappedProductDetailsForm = Form.create({
    mapPropsToFields: ({ values, fields }) =>
        (values &&
            values
                .toSeq()
                .toMap()
                .map((value, key) => {
                    const field = fields && fields.get(key);
                    const seeded =
                        field && field.type === INPUT_TYPES.DATE && value
                            ? moment(value)
                            : value;
                    return Form.createFormField({ value: seeded });
                })
                .toJS()) ||
        {}
})(ProductDetailsForm);

class CreditAppraisalProductDetails extends React.Component {
    state = {
        loading: true,
        screen: null,
        config: null,
        values: Map()
    };

    componentDidMount() {
        this.fetchDetails();
    }

    fetchDetails = () => {
        CaseService.getCreditAppraisalDetails(this.props.caseId)
            .then(({ data }) => {
                this.setState({
                    loading: false,
                    screen: data.product_screen,
                    config: data.config,
                    values: Map(data.details || {})
                });
            })
            .catch(() => {
                this.setState({ loading: false });
            });
    };

    render() {
        const { loading, screen, config, values } = this.state;
        const { bare } = this.props;
        const wrap = (title, body) =>
            bare ? body : <Card title={title}>{body}</Card>;

        if (loading) {
            return wrap("Product Details", <Spin />);
        }

        if (!screen) {
            return null;
        }

        const fields = SCREEN_FIELD_MAP[screen];
        return wrap(
            (config && config.label) || "Product Details",
            <React.Fragment>
                {config && config.note && (
                    <Alert
                        type="info"
                        showIcon
                        message={config.note}
                        className={"mb-10"}
                    />
                )}
                {fields && (
                    <WrappedProductDetailsForm
                        key={screen}
                        caseId={this.props.caseId}
                        fields={fields}
                        values={values}
                        viewOnly={this.props.recreditViewOnlyPermission}
                    />
                )}
            </React.Fragment>
        );
    }
}

CreditAppraisalProductDetails.propTypes = {
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    recreditViewOnlyPermission: PropTypes.bool,
    bare: PropTypes.bool
};

export default CreditAppraisalProductDetails;
